import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, CheckCircle2, Circle, MessageSquareQuote, CalendarDays, Flag, CalendarCheck, Video, Target } from "lucide-react";
import { api } from "../../lib/api";
import { allSlugs } from "./programmeStructure";

function frDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function rdvLabel(startIso, fallback) {
  if (!startIso) return fallback || "";
  const d = new Date(startIso);
  if (isNaN(d)) return fallback || "";
  const o = { timeZone: "Europe/Paris" };
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const wd = new Intl.DateTimeFormat("fr-FR", { ...o, weekday: "short" }).format(d).replace(".", "");
  const day = new Intl.DateTimeFormat("fr-FR", { ...o, day: "numeric" }).format(d);
  const mon = new Intl.DateTimeFormat("fr-FR", { ...o, month: "short" }).format(d).replace(".", "");
  const time = new Intl.DateTimeFormat("fr-FR", { ...o, hour: "2-digit", minute: "2-digit" }).format(d).replace(":", "h");
  return `${cap(wd)}. ${day} ${mon}. · ${time}`;
}

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const { customProg } = useOutletContext() || {};
  const navigate = useNavigate();

  const load = () => api.get("/client/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    await api.put(`/tasks/${t.id}/status`, { status: t.status === "termine" ? "a_faire" : "termine" });
    load();
  };

  if (!data) return <div className="text-ink-muted">Chargement…</div>;
  const { client } = data;
  const all = allSlugs(customProg);
  const completed = (data.completed_slugs || []).filter((s) => all.includes(s));
  const progress = all.length ? Math.round((completed.length / all.length) * 100) : 0;

  return (
    <div className="space-y-10">
      <div className="animate-fade-up">
        <p className="eyebrow">{customProg ? "" : `Semaine ${client.current_week} · `}Phase {client.phase}</p>
        <h1 className="mt-2 font-sans font-medium text-2xl text-ink sm:text-3xl">Bienvenue {client.first_name} sur ton espace membre</h1>
        <p className="mt-3 font-light text-ink-soft">Ravie de te revoir. Voici où tu en es et ce qui t'attend.</p>
      </div>

      {/* Prochain rendez-vous (réservé via Calendly) */}
      {data.deadlines.next_call && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          data-testid="dashboard-next-appointment"
          className="flex flex-col gap-4 rounded-[28px] p-6 text-white shadow-card sm:flex-row sm:items-center sm:justify-between"
          style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 12% 0%, rgba(123,17,58,0.55), transparent 55%), radial-gradient(circle at 100% 100%, rgba(89,2,36,0.6), transparent 50%)" }}
        >
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bordeaux"><CalendarCheck size={22} /></span>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#F3D2D9]">Ton prochain rendez-vous avec Fanny</p>
              <p data-testid="dashboard-next-appointment-when" className="mt-1 font-sans text-sm font-light">{rdvLabel(data.next_call_data?.start_time, data.deadlines.next_call)}</p>
            </div>
          </div>
          {data.next_call_data?.join_url && (
            <a
              href={data.next_call_data.join_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dashboard-join-call"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#8f1544]"
            >
              <Video size={16} /> Rejoindre l'appel
            </a>
          )}
        </motion.div>
      )}

      {/* Dates d'accompagnement */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div data-testid="accompagnement-start" className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 shadow-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><CalendarDays size={18} /></span>
          <div>
            <p className="text-xs text-ink-muted">Début de l'accompagnement</p>
            <p className="text-sm font-medium text-ink">{frDate(data.period?.start)}</p>
          </div>
        </div>
        <div data-testid="accompagnement-end" className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 shadow-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><Flag size={18} /></span>
          <div>
            <p className="text-xs text-ink-muted">Fin de l'accompagnement</p>
            <p className="text-sm font-medium text-ink">{frDate(data.period?.end)}</p>
          </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-2xl border border-line bg-white shadow-card">
        <div className="p-6 sm:p-7">
          <p className="eyebrow">Ton parcours</p>
          <h2 className="mt-2 font-sans font-medium text-2xl text-ink">
            {progress}% du chemin parcouru
          </h2>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-bordeaux-tint">
            <div className="h-full rounded-full bg-bordeaux transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-3 flex justify-between text-xs text-ink-muted">
            <span>Phase 1 · Mise en place</span>
            <span>Phase 2 · Exécution</span>
          </div>
          <button
            data-testid="resume-button"
            onClick={() => navigate("/espace/parcours")}
            className="mt-6 flex items-center gap-2 rounded-full bg-bordeaux px-6 py-3 text-sm font-medium text-white transition hover:bg-bordeaux-dark"
          >
            Continuer l'accompagnement <ArrowRight size={16} />
          </button>
        </div>
      </motion.div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Priorities */}
        <section>
          <h3 className="mb-4 font-sans font-medium text-2xl text-ink">Les priorités</h3>
          <div data-testid="client-priorities-card" className="relative overflow-hidden rounded-[28px] bg-bordeaux p-6 text-white shadow-[0_16px_40px_rgba(123,17,58,0.35)] transition-transform hover:-translate-y-1" style={{ backgroundImage: "radial-gradient(circle at 100% 0%, rgba(243,210,217,0.25), transparent 50%), radial-gradient(circle at 0% 100%, rgba(45,24,30,0.55), transparent 55%)" }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs text-white/85"><span className="h-1.5 w-1.5 rounded-full bg-[#F3D2D9]" /> À faire en priorité</span>
                <p data-testid="client-priorities-count" className="mt-4 font-sans text-5xl font-medium">{data.priorities.filter((t) => t.status === "termine").length}<span className="text-2xl text-white/50"> / {data.priorities.length}</span></p>
                <p className="text-sm text-white/70">priorités accomplies</p>
              </div>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#2D181E]"><Target size={20} /></span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-[#F3D2D9] transition-[width] duration-700" style={{ width: `${data.priorities.length ? (data.priorities.filter((t) => t.status === "termine").length / data.priorities.length) * 100 : 0}%` }} /></div>
            <div className="mt-5 space-y-2">
              {data.priorities.length === 0 && <p className="rounded-2xl bg-white/10 p-4 text-sm text-white/80">Tout est à jour ✨</p>}
              {data.priorities.map((t) => (
                <div key={t.id} data-testid={`client-priority-${t.id}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#2D181E]/40 p-4 backdrop-blur-sm">
                  <button data-testid={`client-task-toggle-${t.id}`} onClick={() => toggle(t)} className="text-[#F3D2D9] transition-transform hover:scale-110">
                    {t.status === "termine" ? <CheckCircle2 size={20} /> : <Circle size={20} className="text-white/50" />}
                  </button>
                  <span className={`text-sm ${t.status === "termine" ? "text-white/50 line-through" : "text-white"}`}>{t.title}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Deadlines */}
        <section>
          <h3 className="mb-4 font-sans font-medium text-2xl text-ink">Les prochaines échéances</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-xl border border-line bg-white p-4 shadow-card">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><Phone size={18} /></span>
              <div>
                <p className="text-xs text-ink-muted">Prochain appel</p>
                <p className="text-sm font-medium text-ink">{data.deadlines.next_call || "À planifier"}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
